type MoneyNode = { amount: string };

export type VariantNode = {
  id: string;
  availableForSale: boolean;
  title: string;
  image?: { url: string } | null;
  price?: MoneyNode | null;
  priceV2?: MoneyNode | null;
  compareAtPrice?: MoneyNode | null;
};

export type GroupProductBasic = {
  handle: string;
  title: string;
  metafield: { value: string | null } | null;
};

export type GroupProductWithVariants = GroupProductBasic & {
  featuredImage?: { url: string } | null;
  variants: { nodes: VariantNode[] };
};

export const STOREFRONT_ENDPOINT = "/api/2025-07/graphql.json";
export const REQUEST_TIMEOUT_MS = 15000;

export const hasKey = <T extends string>(
  obj: unknown,
  key: T
): obj is Record<T, unknown> =>
  typeof obj === "object" && obj !== null && key in obj;

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function isVariantNode(value: unknown): value is VariantNode {
  if (!isRecord(value)) return false;
  return (
    typeof value.id === "string" &&
    typeof value.availableForSale === "boolean" &&
    typeof value.title === "string"
  );
}

export function uniqueByHandle<T extends { handle: string }>(items: T[]): T[] {
  const seenHandles = new Set<string>();
  return items.filter((item) =>
    seenHandles.has(item.handle) ? false : (seenHandles.add(item.handle), true)
  );
}

export function sortByCurrentHandle<
  T extends { handle: string; title: string },
>(items: T[], currentHandle: string): T[] {
  return [...items].sort((a, b) =>
    a.handle === currentHandle
      ? -1
      : b.handle === currentHandle
        ? 1
        : a.title.localeCompare(b.title)
  );
}

export async function fetchGroupProductsBasic(
  groupTag: string
): Promise<GroupProductBasic[]> {
  const token = window.STOREFRONT_TOKEN;
  if (!token) return [];
  const query = `#graphql
    query ProductsByTag($query: String!) {
      products(first: 100, query: $query) {
        nodes {
          handle
          title
          metafield(namespace: "custom", key: "fragrance") { value }
        }
      }
    }
  `;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(STOREFRONT_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": token,
      },
      body: JSON.stringify({
        query,
        variables: { query: `tag:${JSON.stringify(groupTag)}` },
      }),
      signal: controller.signal,
    });
    clearTimeout(timeout);
    if (!res.ok) return [];
    const json: unknown = await res.json();
    if (!hasKey(json, "data")) return [];
    const data = json.data;
    if (!hasKey(data, "products")) return [];
    const products = data.products;
    if (!hasKey(products, "nodes") || !Array.isArray(products.nodes)) return [];
    const nodes: unknown[] = products.nodes;
    return nodes.filter(
      (n): n is GroupProductBasic =>
        isRecord(n) &&
        typeof n.handle === "string" &&
        typeof n.title === "string" &&
        hasKey(n, "metafield")
    );
  } catch {
    clearTimeout(timeout);
    return [];
  }
}

export function isGroupProductWithVariants(
  value: unknown
): value is GroupProductWithVariants {
  if (!isRecord(value)) return false;
  if (typeof value.handle !== "string" || typeof value.title !== "string")
    return false;
  if (!hasKey(value, "variants")) return false;
  const variantsObj = value.variants;
  if (
    !isRecord(variantsObj) ||
    !hasKey(variantsObj, "nodes") ||
    !Array.isArray(variantsObj.nodes)
  )
    return false;
  return variantsObj.nodes.every(isVariantNode);
}

export async function fetchGroupProductsWithVariants(
  groupTag: string
): Promise<GroupProductWithVariants[]> {
  const token = window.STOREFRONT_TOKEN;
  if (!token) return [];
  const query = `#graphql
    query ProductsByTag($query: String!) {
      products(first: 100, query: $query) {
        nodes {
          handle
          title
          featuredImage { url }
          metafield(namespace: "custom", key: "fragrance") { value }
          variants(first: 50) {
            nodes {
              id
              availableForSale
              title
              image { url }
              price { amount }
              priceV2 { amount }
              compareAtPrice { amount }
            }
          }
        }
      }
    }
  `;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(STOREFRONT_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": token,
      },
      body: JSON.stringify({
        query,
        variables: { query: `tag:${JSON.stringify(groupTag)}` },
      }),
      signal: controller.signal,
    });
    clearTimeout(timeout);
    if (!res.ok) return [];
    const json: unknown = await res.json();
    if (!hasKey(json, "data")) return [];
    const data = json.data;
    if (!hasKey(data, "products")) return [];
    const products = data.products;
    if (!hasKey(products, "nodes") || !Array.isArray(products.nodes)) return [];
    const nodesUnknown: unknown[] = products.nodes;
    return nodesUnknown.filter(isGroupProductWithVariants);
  } catch {
    clearTimeout(timeout);
    return [];
  }
}

export function firstAvailableVariant(
  product: GroupProductWithVariants
): VariantNode | null {
  const variants = product.variants?.nodes ?? [];
  const available =
    variants.find((variant) => variant.availableForSale) || variants[0];
  return available || null;
}

export function labelFromMetafield(product: {
  title: string;
  metafield: { value: string | null } | null;
}): string {
  const raw = product.metafield?.value;
  return typeof raw === "string" && raw.trim() ? raw.trim() : product.title;
}

export function amountToCents(amount: string | null | undefined): number {
  if (!amount) return 0;
  const val = parseFloat(amount);
  if (Number.isNaN(val)) return 0;
  return Math.round(val * 100);
}
